/**
 * Compliance Page — SFDR | PAI | Filings
 *
 * Three sections:
 *   - SFDR: fund classification cards
 *   - PAI: select fund -> display 14 indicators table
 *   - Filings: list with workflow status transitions
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  BarChart3,
  FileCheck,
  ChevronDown,
  CheckCircle,
  Plus,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge, TrafficLightBadge, CompletionBar } from '@/components/ui/StatusBadge';
import { useTranslation } from '@/i18n';
import { formatStatus } from '@/utils/formatStatus';
import type {
  Fund,
  ComplianceStatus,
  PAIIndicator,
  ComplianceFiling,
} from '@/types';

type TabKey = 'sfdr' | 'pai' | 'filings';

const SFDR_LABELS: Record<string, string> = {
  article_6: 'Article 6 - No ESG integration',
  article_8: 'Article 8 - Promotes environmental/social',
  article_9: 'Article 9 - Sustainable investment objective',
};

const SFDR_COLORS: Record<string, string> = {
  article_6: 'border-slate-500/30 bg-slate-500/5',
  article_8: 'border-amber-500/30 bg-amber-500/5',
  article_9: 'border-emerald-500/30 bg-emerald-500/5',
};

const FILING_VARIANTS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'default',
  review: 'info',
  approved: 'success',
  filed: 'success',
  rejected: 'danger',
};

const FILING_TRANSITIONS: Record<string, { label: string; action: string }[]> = {
  draft: [{ label: 'Review', action: 'review' }],
  review: [{ label: 'Approve', action: 'approve' }],
  approved: [{ label: 'File', action: 'file' }],
  filed: [],
  rejected: [{ label: 'Review', action: 'review' }],
};

export function CompliancePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabKey>('sfdr');
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [showCreateFiling, setShowCreateFiling] = useState(false);

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const complianceQuery = useQuery<ComplianceStatus[]>({
    queryKey: ['ic', 'dashboard', 'compliance-status'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/dashboard/compliance-status');
      return res.data?.data ?? res.data ?? [];
    },
    enabled: activeTab === 'sfdr',
  });

  const paiQuery = useQuery<PAIIndicator[]>({
    queryKey: ['ic', 'compliance', 'pai', selectedFundId],
    queryFn: async () => {
      const currentYear = new Date().getFullYear();
      const res = await apiClient.get(`/api/v1/impact-core/compliance/pai/${selectedFundId}`, {
        params: { reporting_year: currentYear },
      });
      // Response shape: {status, data: {fund_id, reporting_year, indicator_count, indicators[]}}
      const indicators = res.data?.data?.indicators ?? res.data?.indicators ?? res.data?.data ?? res.data ?? [];
      return Array.isArray(indicators) ? indicators : [];
    },
    enabled: activeTab === 'pai' && !!selectedFundId,
  });

  const filingsQuery = useQuery<ComplianceFiling[]>({
    queryKey: ['ic', 'compliance', 'filings'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/compliance/filings/');
      return res.data?.data ?? res.data ?? [];
    },
    enabled: activeTab === 'filings',
  });

  const filingMutation = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: string }) => {
      const res = await apiClient.post(`/api/v1/impact-core/compliance/filings/${id}/${action}`);
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'compliance', 'filings'] });
    },
  });

  const funds = fundsQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">{t('page.compliance')}</h1>
        <p className="text-xs text-ic-text-tertiary">{t('subtitle.compliance')}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-ic-border">
        <div className="flex gap-0">
          <TabBtn label={t('tab.sfdr')} icon={<ShieldCheck className="h-3.5 w-3.5" />} active={activeTab === 'sfdr'} onClick={() => setActiveTab('sfdr')} />
          <TabBtn label={t('tab.paiIndicators')} icon={<BarChart3 className="h-3.5 w-3.5" />} active={activeTab === 'pai'} onClick={() => setActiveTab('pai')} />
          <TabBtn label={t('tab.filings')} icon={<FileCheck className="h-3.5 w-3.5" />} active={activeTab === 'filings'} onClick={() => setActiveTab('filings')} />
        </div>
      </div>

      {/* SFDR Tab */}
      {activeTab === 'sfdr' && (
        <div className="space-y-4">
          {complianceQuery.isLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-40 animate-pulse rounded-lg bg-slate-700/30" />)}
            </div>
          ) : complianceQuery.isError ? (
            <ErrorMsg msg={t('error.loadCompliance')} />
          ) : (complianceQuery.data ?? []).length === 0 ? (
            <EmptyMsg msg={t('empty.noComplianceData')} />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {(complianceQuery.data ?? []).map((cs) => {
                const fund = funds.find((f) => f.id === cs.fund_id);
                const sfdr = fund?.sfdr_category ?? fund?.sfdr_classification ?? 'article_8';
                return (
                  <div key={cs.fund_id} className={`rounded-lg border p-4 ${SFDR_COLORS[sfdr] ?? 'border-ic-border bg-ic-card'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-semibold text-ic-text-primary">{cs.fund_name}</h3>
                      <StatusBadge
                        label={SFDR_LABELS[sfdr]?.split(' - ')[0] ?? sfdr}
                        variant={sfdr === 'article_9' ? 'success' : sfdr === 'article_8' ? 'warning' : 'default'}
                        size="md"
                      />
                    </div>
                    <p className="text-[10px] text-ic-text-tertiary mb-3">
                      {SFDR_LABELS[sfdr] ?? 'Classification pending'}
                    </p>

                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div className="text-center">
                        <span className="text-[10px] text-ic-text-tertiary block">{t('labels.sfdr')}</span>
                        <TrafficLightBadge status={cs.sfdr_status} />
                      </div>
                      <div className="text-center">
                        <span className="text-[10px] text-ic-text-tertiary block">{t('table.taxonomy')}</span>
                        <TrafficLightBadge status={cs.taxonomy_status} />
                      </div>
                      <div className="text-center">
                        <span className="text-[10px] text-ic-text-tertiary block">{t('compliance.pai')}</span>
                        <TrafficLightBadge status={cs.pai_status} />
                      </div>
                    </div>

                    <CompletionBar percent={cs.data_completeness ?? 0} label={t('compliance.dataCompletenessLabel')} />

                    {cs.next_deadline && (
                      <p className="mt-2 text-[10px] text-ic-text-tertiary">
                        Next deadline: {(() => { try { return format(parseISO(cs.next_deadline), 'dd MMM yyyy'); } catch { return cs.next_deadline; } })()}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* PAI Tab */}
      {activeTab === 'pai' && (
        <div className="space-y-4">
          {/* Fund Selector */}
          <div className="relative max-w-sm">
            <select
              value={selectedFundId}
              onChange={(e) => setSelectedFundId(e.target.value)}
              className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
            >
              <option value="">{t('form.selectFund')}</option>
              {funds.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
          </div>

          {!selectedFundId ? (
            <EmptyMsg msg={t('empty.selectFundPai')} />
          ) : paiQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 7 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
            </div>
          ) : paiQuery.isError ? (
            <ErrorMsg msg={t('error.loadPai')} />
          ) : (paiQuery.data ?? []).length === 0 ? (
            <EmptyMsg msg={t('empty.noPaiIndicators')} />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
              <table className="w-full text-xs ic-table">
                <thead>
                  <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                    <th className="px-4 pb-2 pt-3 font-medium w-10">#</th>
                    <th className="px-4 pb-2 pt-3 font-medium">{t('table.indicator')}</th>
                    <th className="px-4 pb-2 pt-3 font-medium">{t('table.category')}</th>
                    <th className="px-4 pb-2 pt-3 font-medium">{t('table.metric')}</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-right">{t('table.value')}</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-center">{t('table.status')}</th>
                    <th className="px-4 pb-2 pt-3 font-medium text-center">{t('table.mandatory')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(paiQuery.data ?? []).map((indicator) => (
                    <tr key={indicator.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                      <td className="py-2.5 px-4 font-mono text-xs text-ic-text-tertiary">{indicator.number}</td>
                      <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{indicator.name}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{indicator.category}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{indicator.metric}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-primary">
                        {indicator.value != null ? `${indicator.value}${indicator.unit ? ` ${indicator.unit}` : ''}` : '--'}
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        {indicator.status ? <TrafficLightBadge status={indicator.status} /> : <span className="text-xs text-ic-text-tertiary">--</span>}
                      </td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`text-[10px] font-medium ${indicator.mandatory ? 'text-ic-accent' : 'text-ic-text-tertiary'}`}>
                          {indicator.mandatory ? t('general.yes') : t('general.no')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Filings Tab */}
      {activeTab === 'filings' && (
        <div className="space-y-4">
        {/* Create Filing Button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowCreateFiling(true)}
            className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('action.createFiling')}
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
          {filingsQuery.isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
            </div>
          ) : filingsQuery.isError ? (
            <ErrorMsg msg={t('error.loadFilings')} />
          ) : (filingsQuery.data ?? []).length === 0 ? (
            <EmptyMsg
              msg={t('empty.noFilingsYet')}
              action={
                <button
                  onClick={() => setShowCreateFiling(true)}
                  className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('action.createFiling')}
                </button>
              }
            />
          ) : (
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.fund')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.type')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.period')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.status')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.dueDate')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.submitted')}</th>
                  <th className="px-4 pb-2 pt-3 font-medium">{t('table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {(filingsQuery.data ?? []).map((filing) => {
                  const transitions = FILING_TRANSITIONS[filing.status] ?? [];
                  const fundName = filing.fund_name ?? funds.find((f) => f.id === filing.fund_id)?.name ?? '--';
                  return (
                    <tr key={filing.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                      <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{fundName}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{filing.filing_type ?? '--'}</td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{filing.reporting_period ?? filing.period ?? '--'}</td>
                      <td className="py-2.5 px-4">
                        <StatusBadge label={formatStatus(filing.status, t)} variant={FILING_VARIANTS[filing.status] ?? 'default'} />
                      </td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                        {filing.due_date ? (() => { try { return format(parseISO(filing.due_date), 'dd MMM yyyy'); } catch { return filing.due_date; } })() : '--'}
                      </td>
                      <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                        {filing.submitted_date ? (() => { try { return format(parseISO(filing.submitted_date), 'dd MMM yyyy'); } catch { return filing.submitted_date; } })() : '--'}
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-1.5">
                          {transitions.map((t) => (
                            <button
                              key={t.action}
                              onClick={() => filingMutation.mutate({ id: filing.id, action: t.action })}
                              disabled={filingMutation.isPending}
                              className="flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium text-ic-accent hover:bg-ic-accent/10 disabled:opacity-50 transition-colors"
                            >
                              <CheckCircle className="h-3 w-3" />
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Filing Modal */}
        {showCreateFiling && (
          <FilingCreateModal
            funds={funds}
            onClose={() => setShowCreateFiling(false)}
            onCreated={() => {
              setShowCreateFiling(false);
              void queryClient.invalidateQueries({ queryKey: ['ic', 'compliance', 'filings'] });
            }}
          />
        )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function TabBtn({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
        active ? 'border-ic-accent text-ic-accent' : 'border-transparent text-ic-text-tertiary hover:text-ic-text-secondary'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <div className="flex h-48 items-center justify-center rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-red-400">{msg}</p>
    </div>
  );
}

function EmptyMsg({ msg, action }: { msg: string; action?: React.ReactNode }) {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-lg border border-ic-border bg-ic-card">
      <p className="text-xs text-ic-text-tertiary">{msg}</p>
      {action}
    </div>
  );
}

// ─── Filing Create Modal ──────────────────────────────────────

const REGULATION_OPTIONS: { value: string; label: string }[] = [
  { value: 'sfdr', label: 'SFDR' },
  { value: 'eu_taxonomy', label: 'EU Taxonomy' },
  { value: 'csrd', label: 'CSRD' },
  { value: 'pai_statement', label: 'PAI Statement' },
];
const FILING_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'pre_contractual', label: 'Pre-Contractual' },
  { value: 'periodic', label: 'Periodic' },
  { value: 'website', label: 'Website' },
  { value: 'pai_statement', label: 'PAI Statement' },
  { value: 'annual', label: 'Annual' },
  { value: 'entity_level', label: 'Entity Level' },
  { value: 'product_level', label: 'Product Level' },
];

function FilingCreateModal({ funds, onClose, onCreated }: { funds: Fund[]; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [fundId, setFundId] = useState('');
  const [regulation, setRegulation] = useState('sfdr');
  const [filingType, setFilingType] = useState('pre_contractual');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async (payload: { fund_id: number; regulation: string; filing_type: string; reporting_period: string }) => {
      const res = await apiClient.post('/api/v1/impact-core/compliance/filings/', payload);
      return res.data;
    },
    onSuccess: () => {
      onCreated();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string | Array<{ msg: string; loc: string[] }> } } };
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'string') {
        setErrors({ _general: detail });
      } else if (Array.isArray(detail)) {
        const mapped: Record<string, string> = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1] ?? '_general';
          mapped[field] = item.msg;
        }
        setErrors(mapped);
      } else {
        setErrors({ _general: t('error.createFiling') });
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!fundId) newErrors.fund_id = t('error.pleaseselectFund');
    if (!reportingPeriod) newErrors.reporting_period = t('error.reportingPeriodRequired');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    createMutation.mutate({
      fund_id: Number(fundId),
      regulation,
      filing_type: filingType,
      reporting_period: reportingPeriod,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">{t('action.createFiling')}</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <FilingFieldGroup label={`${t('table.fund')} *`} error={errors.fund_id}>
            <div className="relative">
              <select
                value={fundId}
                onChange={(e) => setFundId(e.target.value)}
                className="w-full appearance-none rounded-md border border-ic-border bg-ic-input px-3 py-2 pr-8 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="">{t('form.selectFund')}</option>
                {funds.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary pointer-events-none" />
            </div>
          </FilingFieldGroup>

          <div className="grid grid-cols-2 gap-4">
            <FilingFieldGroup label={t('form.regulation')} error={errors.regulation}>
              <select
                value={regulation}
                onChange={(e) => setRegulation(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                {REGULATION_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </FilingFieldGroup>

            <FilingFieldGroup label={t('form.filingType')} error={errors.filing_type}>
              <select
                value={filingType}
                onChange={(e) => setFilingType(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                {FILING_TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </FilingFieldGroup>
          </div>

          <FilingFieldGroup label={`${t('form.reportingPeriod')} *`} error={errors.reporting_period}>
            <input
              type="text"
              value={reportingPeriod}
              onChange={(e) => setReportingPeriod(e.target.value)}
              placeholder="e.g. 2026-Q1 or 2026"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </FilingFieldGroup>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            {t('action.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? t('action.create') + '...' : t('action.createFiling')}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilingFieldGroup({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
